"use client";

import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceBenefitItem,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

const pmImage = "/images/services/svc-product-management.jpg";
const pmApproachImage = "/images/services/pm.jpg";

// Cancels the layout container padding so band sections read full-width.
const bandBleed = "-mx-4 sm:-mx-6 lg:-mx-8";

const engagementModels = [
  {
    title: "Fractional Product Manager",
    rows: [
      {
        label: "Scope",
        value:
          "Senior product leadership a few days a week — owning strategy, roadmap and prioritization without a full-time hire.",
      },
      {
        label: "Typical duration",
        value: "3-12 months, 1-3 days per week",
      },
      {
        label: "Best for",
        value:
          "Startups and SMEs that need experienced product direction while staying lean.",
      },
    ],
  },
  {
    title: "Embedded Product Manager",
    rows: [
      {
        label: "Scope",
        value:
          "A dedicated product manager inside your team — running discovery, sprint planning and stakeholder alignment day to day.",
      },
      {
        label: "Typical duration",
        value: "6-18 months, full-time",
      },
      {
        label: "Best for",
        value:
          "Scale-ups and enterprises delivering a major product launch or platform rebuild.",
      },
    ],
  },
];

const pmFAQs = [
  {
    question: "How much do product management services cost in Malaysia?",
    answer:
      "A fractional product manager typically costs RM 8,000-18,000 per month depending on days per week, while an embedded full-time engagement ranges from RM 20,000-35,000 per month based on seniority and scope. Both options cost significantly less than recruiting, since you skip hiring risk and ramp-up time.",
  },
  {
    question:
      "What is the difference between fractional and embedded product management?",
    answer:
      "A fractional product manager works with your team part-time — usually 1-3 days a week — focusing on strategy, roadmap and prioritization. An embedded product manager joins your team full-time and runs day-to-day delivery, from discovery and user stories to sprint execution. Many Malaysian clients start fractional and move to embedded as the product scales.",
  },
  {
    question: "Can your product managers work with our existing development team?",
    answer:
      "Yes. Our product managers integrate with in-house, outsourced or hybrid development teams, and are experienced with agile workflows in tools like Jira, Linear and Azure DevOps. They align engineering, design and business stakeholders without disrupting how your team already ships.",
  },
  {
    question: "How quickly can a product manager start and show results?",
    answer:
      "We can usually start within 1-2 weeks of scoping. In the first 30 days you can expect a clarified product roadmap, a prioritized backlog, and a measurable delivery cadence — the foundations that compound into faster, more confident releases.",
  },
  {
    question: "Do you only manage products, or can you build them too?",
    answer:
      "Both. Anchor Sprint is a full-service studio with software development, design and AI engineering teams, so the same engagement can cover product strategy and the team that ships it — one accountable partner from roadmap to release.",
  },
];

export default function ProductManagement() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const translatedFaqs = pmFAQs.map((faq) => ({
    question: translate(faq.question),
    answer: translate(faq.answer),
  }));

  return (
    <ServicePageLayout
      title={translate("Product Management Services in Malaysia")}
      breadcrumb={translate("Services / Product Management")}
      currentServiceId="pm"
      accent="Product Management"
      dek="Senior product leadership — fractional or embedded — that takes your digital product from idea to market."
    >
      <ServiceHeroImage src={pmImage} alt="Product Management Services Malaysia" />

      <div className="mb-16">
        <h2 className="mb-6 font-display text-3xl font-bold text-neutral-emphasis">
          {translate("Build the right product, not just a working one")}
        </h2>
        <p className="max-w-2xl leading-relaxed text-neutral-muted">
          {translate(
            "Anchor Sprint offers comprehensive product management services to help Malaysian businesses develop and launch successful digital products. Our expert team combines market insights, technical expertise, and strategic thinking to guide products from conception to market success.",
          )}
        </p>
      </div>

      <ServiceSection
        title="Our Services"
        titleLevel="h2"
        eyebrow="What we do"
        accent="Services"
        dek="Strategy, delivery and analytics — the full product lifecycle under one engagement."
        band="ivory"
        className={bandBleed}
      >
        <ServiceGrid columns={3}>
          <ServiceFeatureCard
            icon="fas fa-lightbulb"
            title="Product Strategy"
            description=""
            features={[
              "Market research and analysis",
              "Product vision and roadmap development",
              "Competitive analysis",
              "Go-to-market strategy",
            ]}
            variant="inline-icon"
          />

          <ServiceFeatureCard
            icon="fas fa-tasks"
            title="Product Development"
            description=""
            features={[
              "Feature prioritization",
              "Sprint planning and execution",
              "User story development",
              "Development team coordination",
            ]}
            variant="inline-icon"
          />

          <ServiceFeatureCard
            icon="fas fa-chart-line"
            title="Product Analytics"
            description=""
            features={[
              "User behavior analysis",
              "Performance metrics tracking",
              "A/B testing",
              "Data-driven decision making",
            ]}
            variant="inline-icon"
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="Engagement Models"
        titleLevel="h2"
        eyebrow="Ways to work with us"
        accent="Engagement Models"
        dek="Two ways to plug senior product leadership into your team — choose the level of involvement your roadmap needs."
        band="surface"
        className={bandBleed}
      >
        <div className="grid gap-6 lg:grid-cols-2">
          {engagementModels.map((model) => (
            <div
              key={model.title}
              className="rounded-2xl border border-border bg-card p-8"
            >
              <h3 className="font-display text-2xl font-semibold text-neutral-emphasis">
                {translate(model.title)}
              </h3>
              <dl className="mt-6 divide-y divide-border border-t border-border">
                {model.rows.map((row) => (
                  <div key={row.label} className="py-4">
                    <dt className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-muted">
                      {translate(row.label)}
                    </dt>
                    <dd className="mt-2 leading-relaxed text-neutral-emphasis">
                      {translate(row.value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </ServiceSection>

      <ServiceSection
        title="Our Approach"
        titleLevel="h2"
        eyebrow="How we work"
        accent="Approach"
        dek="A four-step cycle that keeps every release anchored to user needs and business outcomes."
        band="ivory"
        className={bandBleed}
      >
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="grid grid-cols-1 gap-4">
            <ServiceBenefitItem
              title="Discovery"
              description="Understanding market needs and opportunities through research and analysis."
              icon="fas fa-check-circle"
            />

            <ServiceBenefitItem
              title="Planning"
              description="Developing detailed product strategies and roadmaps."
              icon="fas fa-check-circle"
            />

            <ServiceBenefitItem
              title="Execution"
              description="Managing development process and stakeholder coordination."
              icon="fas fa-check-circle"
            />

            <ServiceBenefitItem
              title="Optimization"
              description="Continuous improvement based on data and user feedback."
              icon="fas fa-check-circle"
            />
          </div>
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src={pmApproachImage}
              alt={translate("Product team planning a roadmap during a workshop")}
              loading="lazy"
              width={800}
              height={600}
              decoding="async"
              className="h-full max-h-[480px] w-full object-cover"
            />
          </div>
        </div>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        titleLevel="h2"
        eyebrow="FAQ"
        band="surface"
        className={bandBleed}
      >
        <ServiceFaq items={pmFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={translatedFaqs} />

      <ConsultationCTA
        title="Ready to Build Successful Digital Products?"
        description="Let's discuss how our product management expertise can help bring your product vision to life."
      />
    </ServicePageLayout>
  );
}
